"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared";
import {
  type ClassEnrollmentFetchScope,
  fetchEnrollmentsForClasses,
} from "@/lib/dashboard/teacher-class-enrollments";
import { QueryKeys } from "@/lib/types/query-keys";

export function useTeacherClassEnrollments(
  classIds: number[],
  options?: {
    enabled?: boolean;
    scope?: ClassEnrollmentFetchScope;
    refetchOnMount?: boolean | "always";
  },
) {
  const accessToken = useAccessToken();
  const sortedKey = [...classIds].sort((a, b) => a - b).join(",");
  const scope = options?.scope ?? "all";

  const { data, isError, isPending, isFetching, isLoading, refetch } = useQuery(
    {
      queryKey: [QueryKeys.TeacherClassEnrollments, sortedKey, scope],
      enabled:
        (options?.enabled ?? true) && !!accessToken && classIds.length > 0,
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Missing access token for teacher class enrollments");
        }
        return fetchEnrollmentsForClasses(accessToken, classIds, scope);
      },
      staleTime: 60_000,
      ...(options?.refetchOnMount != null
        ? { refetchOnMount: options.refetchOnMount }
        : {}),
    },
  );

  return { data, isError, isPending, isFetching, isLoading, refetch };
}
